import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot, limit, doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import './AdminPanel.css';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, 
  MousePointer2, 
  MapPin, 
  ArrowLeft,
  Activity,
  Globe,
  Database,
  Lock,
  Unlock,
  ShieldAlert,
  ShieldCheck,
  LogOut,
  AlertCircle
} from 'lucide-react';

const AdminPanel = () => {
  const [isAuthorized, setIsAuthorized] = useState(
    sessionStorage.getItem('admin_authorized') === 'true'
  );
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [stats, setStats] = useState({ totalVisits: 0 });
  const [profiles, setProfiles] = useState([]);
  const [recentInteractions, setRecentInteractions] = useState([]);
  const [recentSessions, setRecentSessions] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);

  // Authorization Check
  const handleLogin = (e) => {
    e.preventDefault();
    const securePass = process.env.REACT_APP_ADMIN_PASSWORD;
    if (password === securePass) {
      setIsAuthorized(true);
      sessionStorage.setItem('admin_authorized', 'true');
      setError('');
    } else {
      setError('Invalid master password. Intruder alert!');
      setPassword('');
    }
  };

  const handleLogout = () => {
    setIsAuthorized(false);
    sessionStorage.removeItem('admin_authorized');
  };

  useEffect(() => {
    if (!isAuthorized) return;

    // Real-time listener for global stats
    const globalStatsUnsub = onSnapshot(query(collection(db, 'global_stats')), (snapshot) => {
      snapshot.forEach(doc => {
        if (doc.id === 'website_visits') setStats(doc.data());
      });
    });

    // Real-time listener for profiles
    const profilesUnsub = onSnapshot(
      query(collection(db, 'visitor_profiles'), orderBy('lastSeenAt', 'desc'), limit(100)), 
      (snapshot) => {
        setProfiles(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        setIsLoading(false);
      }
    );

    // Real-time listener for sessions
    const sessionsUnsub = onSnapshot(
      query(collection(db, 'visit_sessions'), orderBy('sessionStart', 'desc'), limit(50)),
      (snapshot) => {
        setRecentSessions(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      }
    );

    // Real-time listener for interactions
    const interactionsUnsub = onSnapshot(
      query(collection(db, 'interactions'), orderBy('timestamp', 'desc'), limit(50)),
      (snapshot) => {
        setRecentInteractions(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      }
    );

    return () => {
      globalStatsUnsub();
      profilesUnsub();
      sessionsUnsub();
      interactionsUnsub();
    };
  }, [isAuthorized]);

  const toggleBlockUser = async (id, currentStatus) => {
    try {
      const profileRef = doc(db, 'visitor_profiles', id);
      await setDoc(profileRef, { blocked: !currentStatus }, { merge: true });
    } catch (err) {
      console.error("Failed to update block status:", err);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Just now';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  if (!isAuthorized) {
    return (
      <div className="admin-login-container">
        <div className="background-effects">
          <div className="gradient-orb orb-1"></div>
          <div className="gradient-orb orb-2"></div>
        </div>
        <motion.div 
          className="login-card"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div className="lock-icon-container">
            <Lock size={40} strokeWidth={1.5} />
          </div>
          <h1>System <span className="title-gradient">Auth</span></h1>
          <p>Accessing the command center requires the master key.</p>
          
          <form onSubmit={handleLogin}>
            <div className="input-group">
              <input 
                type="password" 
                placeholder="Enter Admin Password..." 
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
              />
              <button type="submit" className="login-btn">AUTHORIZE</button>
            </div>
            {error && <motion.div className="error-msg" initial={{y:10}} animate={{y:0}}><AlertCircle size={14}/> {error}</motion.div>}
          </form>
          
          <button className="back-to-site" onClick={() => window.location.href = '/'}>
            Back to Portfolio
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="background-effects">
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
      </div>

      <div className="admin-content">
        <div className="admin-header">
          <div className="header-top">
            <button className="back-btn" onClick={() => window.location.href = '/'}>
              <ArrowLeft size={16} /> Portfolio
            </button>
            <div className="header-actions">
              <div className="status-badge">
                <span className="pulse"></span> LIVE
              </div>
              <button className="logout-btn" onClick={handleLogout}>
                <LogOut size={16} />
              </button>
            </div>
          </div>
          <h1>Command <span className="title-gradient">Center</span></h1>
          <p className="admin-subtitle">Monitoring real-time interactions across your portfolio.</p>
        </div>

        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon visits"><Users /></div>
            <div className="stat-info">
              <h3>Total Visits</h3>
              <h2>{stats.totalVisits || 0}</h2>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon profiles"><Database /></div>
            <div className="stat-info">
              <h3>Stored Profiles</h3>
              <h2>{profiles.length}</h2>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon active"><Activity /></div>
            <div className="stat-info">
              <h3>Recent Actions</h3>
              <h2>{recentInteractions.length}</h2>
            </div>
          </div>
        </div>

        <div className="admin-tabs">
          <button className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>
            <Globe size={18} /> Sessions
          </button>
          <button className={`tab-btn ${activeTab === 'visitors' ? 'active' : ''}`} onClick={() => setActiveTab('visitors')}>
            <Users size={18} /> Profiles
          </button>
          <button className={`tab-btn ${activeTab === 'interactions' ? 'active' : ''}`} onClick={() => setActiveTab('interactions')}>
            <MousePointer2 size={18} /> Behavior
          </button>
        </div>

        <div className="admin-main">
          {isLoading ? (
            <div className="loader-container">
              <div className="loader"></div>
            </div>
          ) : (
            <AnimatePresence mode='wait'>
              {activeTab === 'overview' && (
                <motion.div key="overview" layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="tab-content">
                  <div className="admin-table-wrapper">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Visitor</th>
                          <th>Device Summary</th>
                          <th>Referrer</th>
                          <th>Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentSessions.map(session => {
                          const profile = profiles.find(p => p.id === session.visitorId);
                          return (
                            <tr key={session.id} className={profile?.blocked ? 'row-blocked' : ''}>
                              <td>
                                <div className="visitor-meta">
                                  <strong>{profile?.actualNameCaught || profile?.presumedName || 'Anonymous'}</strong>
                                  <span className="tiny-id">{session.visitorId} {profile?.blocked && <span className="blocked-tag">BLOCKED</span>}</span>
                                </div>
                              </td>
                              <td><span className="tag device">{session.deviceSummary || 'Desktop'}</span></td>
                              <td><span className="referrer">{session.referrer || 'direct'}</span></td>
                              <td>{formatDate(session.sessionStart)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </motion.div>
              )}

              {activeTab === 'visitors' && (
                <motion.div key="visitors" layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="tab-content">
                  <div className="admin-table-wrapper">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Identity / Location</th>
                          <th>System Specs</th>
                          <th>Blocked?</th>
                          <th>Visits</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {profiles.map(p => (
                          <tr key={p.id} className={p.blocked ? 'row-blocked' : ''}>
                            <td>
                              <div className="visitor-meta">
                                <strong>{p.actualNameCaught || p.presumedName}</strong>
                                <span>{p.actualEmailCaught || p.latestLocationData?.ip || p.id}</span>
                                <span className="location-tag"><MapPin size={12}/> {p.latestLocationData?.city || 'Unknown'}</span>
                              </div>
                            </td>
                            <td>
                              <div className="device-meta">
                                <span className="tag os">{p.latestDeviceData?.platform}</span>
                                <span className="tag ram">{p.latestDeviceData?.deviceMemory}GB RAM</span>
                              </div>
                            </td>
                            <td>
                              <span className={`status-pill ${p.blocked ? 'blocked' : 'active'}`}>
                                {p.blocked ? <ShieldAlert size={14}/> : <ShieldCheck size={14}/>}
                                {p.blocked ? 'Blocked' : 'Active'}
                              </span>
                            </td>
                            <td><span className="visit-count">{p.personalVisitCount}x</span></td>
                            <td>
                              <button 
                                className={`block-toggle-btn ${p.blocked ? 'unblock' : 'block'}`}
                                onClick={() => toggleBlockUser(p.id, p.blocked)}
                              >
                                {p.blocked ? <Unlock size={14}/> : <ShieldAlert size={14}/>}
                                {p.blocked ? 'Unblock' : 'Block'}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </motion.div>
              )}

              {activeTab === 'interactions' && (
                <motion.div key="interactions" layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="tab-content">
                  <div className="admin-table-wrapper">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Visitor</th>
                          <th>Action</th>
                          <th>Details</th>
                          <th>Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentInteractions.map(i => {
                          const profile = profiles.find(p => p.id === i.visitorId);
                          return (
                            <tr key={i.id} className={profile?.blocked ? 'row-blocked' : ''}>
                              <td><strong>{profile?.actualNameCaught || 'Anonymous'}</strong></td>
                              <td><span className={`action-badge ${i.action}`}>{i.action.replace('_', ' ')}</span></td>
                              <td><code className="interaction-json">{JSON.stringify(i.details)}</code></td>
                              <td>{formatDate(i.timestamp)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
